import React, { useRef, useCallback, useState } from "react";
import { parse } from "best-effort-json-parser";
import { AgGridReact } from "ag-grid-react";
import { useWindowSize } from "@uidotdev/usehooks";
import { subDays } from "date-fns";
import { RefreshCw } from "lucide-react";

import Spacer from "../../Components/Spacer";
import Spinner from "../../Components/Spinner";
import { useAIStream } from "../../Hooks/useAiStream";

import css from "./styles.styl";
import "ag-grid-community/styles/ag-grid.css"; // Core CSS
import "ag-grid-community/styles/ag-theme-quartz.css"; // Theme

const DEFAULT_COLUMN_DEF = {
	sortable: true,
	floatingFilter: true,
	filter: true,
};

const statusBar = {
	statusPanels: [
		{ statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
		{ statusPanel: "agTotalRowCountComponent", align: "center" },
		{ statusPanel: "agFilteredRowCountComponent" },
		{ statusPanel: "agSelectedRowCountComponent" },
		{ statusPanel: "agAggregationComponent" },
	],
};

const ViewFullLogRenderer = ({ data }) => {
	const handleViewFullLog = () => {
		// Create a new window
		const newWindow = window.open("", "_blank");

		// Create formatted JSON content with syntax highlighting
		const formattedContent = `
			<!DOCTYPE html>
			<html>
				<head>
					<title>Full Log Details</title>
					<style>
						body { 
							background: #1e1e1e;
							color: #d4d4d4;
							font-family: monospace;
							padding: 20px;
							margin: 0;
						}
						pre {
							white-space: pre-wrap;
							word-wrap: break-word;
						}
					</style>
				</head>
				<body>
					<pre>${JSON.stringify(data, null, 2)}</pre>
				</body>
			</html>
		`;

		// Write the content to the new window
		newWindow.document.write(formattedContent);
		newWindow.document.close();
	};

	return (
		<div style={{ display: "flex", height: "100%", width: "100%", alignItems: "center", justifyContent: "center" }}>
			<button className={css.viewFullLogButton} type="button" onClick={handleViewFullLog}>
				View Full Log
			</button>
		</div>
	);
};

const COLUMNS = [
	{
		field: "message.time",
		headerName: "Date",
		sort: "desc",
		filter: "agDateColumnFilter",
		valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString() : ""),
	},
	{
		field: "message.detail-type",
		headerName: "Type",
	},
	{
		field: "message.detail.mail.destination",
		headerName: "To",
		filter: "agTextColumnFilter",
	},
	{
		field: "message.detail.mail.tags.company_id",
		headerName: "Company ID",
		filter: "agTextColumnFilter",
	},
	{
		field: "message.detail.mail.tags.email_type",
		headerName: "Email Type",
		filter: "agTextColumnFilter",
	},
	{
		field: "remove",
		headerName: "Remove",
		cellRenderer: ViewFullLogRenderer,
		filter: false,
		sortable: false,
		suppressMenu: true,
		floatingFilter: false,
		suppressMovable: true,
		checkboxSelection: false,
		lockPosition: true,
		width: 100,
	},
];

/**
 * Parses the stream data into a JSON array.
 * @param {string} data - The stream data to parse.
 * @returns {Array} The parsed data.
 */
const parseStream = (data) => {
	const parsed = parse(data);

	// if parsed is not items, return []
	if (parsed === "") return [];

	return parsed.map((item) => {
		let message = {};

		try {
			message = JSON.parse(item.message);
		} catch (e) {
			console.error(e);
		}

		return {
			...item,
			message: {
				...message,
				time: new Date(message.time),
			},
		};
	});
};

const SESLogsPage = () => {
	const gridRef = useRef(null);
	const size = useWindowSize();

	/**
	 * A configuration for the date range of the logs. Currently static.
	 */
	const [dateRange, setDateRange] = useState({
		from: subDays(new Date(), 30).toISOString(),
		to: new Date().toISOString(),
	});

	const url = `/production/ses-logs?from=${dateRange.from}&to=${dateRange.to}`;
	const { data, isLoading, makeRequest } = useAIStream({
		url: url,
	});

	/**
	 * Init the fetch of the data from the API.
	 */
	React.useEffect(() => {
		makeRequest();
	}, [makeRequest]);

	const parsedData = parseStream(data);

	/**
	 * Auto-size all columns.
	 */
	const autoSizeAll = useCallback(() => {
		gridRef.current.api.autoSizeAllColumns();
	}, []);

	// Set up a recurring job to auto-size columns while data is loading
	React.useEffect(() => {
		let intervalId = null;

		if (isLoading) {
			// Run autoSizeAll every 3 seconds while loading
			intervalId = setInterval(() => {
				if (gridRef.current?.api) {
					autoSizeAll();
				}
			}, 1_000);
		}

		// Clean up the interval when loading stops or component unmounts
		return () => {
			if (intervalId) {
				clearInterval(intervalId);
			}
		};
	}, [isLoading, autoSizeAll]);

	console.log(parsedData);

	return (
		<div className={css.content}>
			<div className={css.sectionInformation}>
				<div className={css.title}>
					<span>AWS Email Logs</span>
					<button className={css.refreshButton} onClick={() => makeRequest()} type="button">
						<RefreshCw size={16} />
					</button>
				</div>

				<Spacer size={2} />

				<div className={css.gridContainer}>
					{isLoading && (
						<div style={{ position: "absolute", right: "14px", bottom: "14px", zIndex: 10 }}>
							<Spinner />
						</div>
					)}
					<div className="ag-theme-quartz" style={{ height: size.height - 180, position: "relative" }}>
						<AgGridReact
							ref={gridRef}
							animateRows={false}
							defaultColDef={DEFAULT_COLUMN_DEF}
							rowData={parsedData}
							columnDefs={COLUMNS}
							isRowSelectable={() => false}
							statusBar={statusBar}
							enableRangeSelection={true}
							suppressContextMenu={false}
							rowSelection="multiple"
							gridOptions={{
								suppressScrollOnNewData: true,
								autoSizeStrategy: {
									type: "fitCellContents",
								},
							}}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SESLogsPage;
